import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { AppErrorCode as UaaAppErrorCode } from '@renovationman/rm-uaa-api-client-ts-ng20';
import { IJwtUser, JwtUser, UserType } from '@renovationman/ts-utils';
import {
  AuthService,
  AuthType,
  ORIGIN_URL_QUERY_PARAM_KEY,
  PREFILL_EMAIL_QUERY_PARAM_KEY,
} from 'libs/auth-lib/src/public-api';
import { first } from 'rxjs/operators';

import { APP_TYPE_QUERY_PARAM_KEY, COOKIES_REQUIRED_PATH } from '~constants';

import { ApiErrorToastService } from '../../services/api-error-toast.service';
import { rmRedirect } from '../../services/rm-redirect';
import {
  AuthorizationSession,
  AuthorizationSessionService,
} from '../../services/authorization-session.service';
import { sessionCookieAccepted } from '../../services/session-cookie-probe';
import { UserDestinationService } from '../../services/user-destination.service';

/**
 * Les messages que la page sait donner sur un échec de connexion. Tout le reste — compte bloqué par
 * l'anti-bruteforce, corps HTML d'une panne CDN, erreur inattendue — reçoit le message générique.
 */
const LOGIN_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  [UaaAppErrorCode.INCORRECT_CREDENTIALS]: 'API_ERRORS.INCORRECT_CREDENTIALS',
};
const DEFAULT_LOGIN_ERROR_MESSAGE_KEY = 'API_ERRORS.DEFAULT';

@Component({
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: false,
})
export class LoginPage implements OnInit {
  form = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
  });

  appType?: AuthType;

  /**
   * Deux soumissions ouvriraient deux sessions, et la seconde chasserait la requête que la première
   * avait mise de côté : la reprise rendrait alors « authorization request not found ».
   */
  submitting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private authorizationSessionService: AuthorizationSessionService,
    private userDestinationService: UserDestinationService,
    private apiErrorToastService: ApiErrorToastService
  ) {}

  get isClient() {
    return this.appType === AuthType.CLIENT;
  }

  /**
   * <strong>Arriver ici veut dire vouloir se connecter</strong>, quel que soit ce qui traîne dans le
   * stockage. Cette page court-circuitait autrefois vers l'espace quand un jeton y était rangé ;
   * combiné à la remise sans jeton, ce raccourci ferait boucler espace → portail → espace
   * indéfiniment — l'espace ne voit pas le stockage du portail, donc il redemanderait une
   * autorisation à chaque tour, et le portail le renverrait à chaque tour.
   *
   * Plus rien ne range de jeton ici : ni la reprise de flux, ni la remise sans jeton, ni la route de
   * rappel du portail, qui le documente déjà. L'effacement reste pour ce qu'une version précédente a
   * pu laisser dans ce navigateur.
   */
  ngOnInit(): void {
    this.authService.eraseStoredToken();

    this.route.queryParamMap.pipe(first()).subscribe((queryParamMap) => {
      this.appType = queryParamMap.get(APP_TYPE_QUERY_PARAM_KEY) as AuthType;
      const prefillEmail = queryParamMap.get(
        PREFILL_EMAIL_QUERY_PARAM_KEY
      ) as string;
      if (prefillEmail) {
        this.form.controls.email.setValue(decodeURIComponent(prefillEmail));
      }
    });
  }

  submit(): void {
    this.form.markAllAsTouched();
    const values = this.form.value;
    if (
      this.submitting ||
      this.form.invalid ||
      !values.email ||
      !values.password
    ) {
      return;
    }
    this.submitting = true;
    this.authorizationSessionService
      .open(values.email, values.password)
      .subscribe((session) => this.leaveOrStay(session));
  }

  /**
   * Après l'ouverture de session : partir par l'une de deux portes, ou rester.
   *
   * <table>
   * <tr><td>Un flux attendait</td><td>on le reprend</td></tr>
   * <tr><td>Session ouverte, aucun flux</td><td>on remet à l'espace <strong>sans jeton</strong></td></tr>
   * <tr><td>Échec</td><td>on reste, avec un message</td></tr>
   * </table>
   *
   * <strong>Une seule authentification, plus deux.</strong> La page appelait d'abord le login
   * historique (`POST /auth_manager/access_tokens`) pour lire le type d'utilisateur dans son jeton et
   * garder une redirection `?token=` en filet si la session tombait. La session rend désormais ce type
   * elle-même — story O2.12 —, et le filet ne protégeait que l'arrivée sans contexte : toute connexion
   * qui reprend un flux, depuis n'importe quel espace, dépend déjà de cette session sans aucun repli.
   * C'était le dernier appel front au chemin hérité.
   */
  private leaveOrStay(session: AuthorizationSession): void {
    if (session.resumeUri) {
      rmRedirect(session.resumeUri);
      return;
    }
    if (session.opened) {
      this.handOffToSpace(session.userType);
      return;
    }
    this.submitting = false;
    this.form.controls.password.reset();
    this.apiErrorToastService.show(
      LOGIN_ERROR_MESSAGE_KEYS[session.errorCode ?? ''] ??
        DEFAULT_LOGIN_ERROR_MESSAGE_KEY
    );
  }

  /**
   * Remet la personne à son espace sans rien poser dans l'URL.
   *
   * L'espace n'y trouve aucun jeton, amorce son propre flux `authorization_code`, et le code revient
   * <strong>sans écran</strong> puisque la session SSO vient d'être ouverte. Elle repart avec le jeu
   * complet — accès, rafraîchissement, identité — là où le `?token=` hérité ne lui donnait qu'un
   * jeton d'accès, dans l'historique du navigateur, l'en-tête `Referer` et les journaux CloudFront
   * et ALB.
   *
   * <strong>Vérifier avant de partir, plutôt que détecter le retour.</strong> Si le navigateur refuse
   * le cookie de session, `/oauth2/authorize` ne trouvera jamais rien et renverra ici à chaque tour.
   * On le lui demande donc avant la remise, par un cookie témoin : refusé, c'est l'écran
   * d'explication, sans aller-retour. Détecter le rebond au retour ne marchait pas — la déconnexion
   * OIDC revient ici de la même façon, et une reconnexion rapide passait pour un rebond.
   */
  private handOffToSpace(userType: UserType | null): void {
    if (!sessionCookieAccepted()) {
      this.router.navigate([COOKIES_REQUIRED_PATH]);
      return;
    }
    // `UserDestinationService` aiguille sur les accesseurs de `JwtUser` (un CT ou un SDR va chez les
    // employés) : on lui en donne un, bâti sur le seul type, plutôt que de recopier cette table.
    const jwtUser = userType
      ? new JwtUser({ type: userType } as IJwtUser)
      : null;
    rmRedirect(this.destinationFor(jwtUser).href);
  }

  /**
   * Où envoyer cette personne — l'`originUrl` demandée si elle désigne l'un de nos espaces, sinon
   * l'espace que dit son type.
   *
   * <strong>L'utilisateur est un paramètre, pas une lecture du stockage.</strong> La remise sans
   * jeton ne range rien : elle n'a que ce que l'ouverture de session vient de rendre.
   */
  private destinationFor(jwtUser: JwtUser | null): URL {
    const originUrl = this.route.snapshot.queryParamMap.get(
      ORIGIN_URL_QUERY_PARAM_KEY
    );
    return (
      this.requestedOriginUrl(originUrl) ??
      this.userDestinationService.urlFor(jwtUser)
    );
  }

  /**
   * L'`originUrl` demandée, si et seulement si elle désigne l'un de nos espaces.
   *
   * Plus aucun jeton ne part dans cette URL, mais le filtre reste nécessaire : sans lui, `?originUrl=`
   * ferait du portail une redirection ouverte, qui est un outil d'hameçonnage en soi. On retombe sur
   * la destination déduite du type d'utilisateur, qui est toujours l'une des nôtres.
   */
  private requestedOriginUrl(originUrl: string | null): URL | null {
    if (!originUrl) {
      return null;
    }
    try {
      const url = new URL(originUrl);
      return this.userDestinationService.isKnownSpaceUrl(url) ? url : null;
    } catch {
      return null;
    }
  }
}
